import { GridCellParams } from '@mui/x-data-grid-pro';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { v4 } from 'uuid';
import { GridCustomColDef } from '../../types';
import {
  advancedOperators,
  advancedDatePickerOperator,
  advancedBooleanOperators,
  advancedNumericOperators,
  advancedEnumOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { getDate, getFormattedDate } from '../../../utils/helpers';
import { TicketStatus } from '../../../store/Ticket';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers';

export const DefaultTicketGridColumnDefs: GridCustomColDef[] = [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={{
          id: v4(),
          data: params.row,
          ...getMetaData({ name: ComponentName.TICKET, id: params.row.id, data: params.row })
        }}
      />
    ),
    hideable: false,
    pinned: 'left',
    disableReorder: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'number',
    headerName: 'Number',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/ticket/${params.row.id}`}>
        <div className="flex items-center text-small">{params.value as string}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    pinned: 'left',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'supplierName',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'tsdName',
    headerName: 'TSD',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: Object.values(TicketStatus ?? {}),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'status',
    headerName: 'Status',
    sortable: true,
    filterable: true,
    width: 300,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'inquiryType',
    headerName: 'Inquiry Type',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'assignedTo.email',
    headerName: 'Assigned To',
    sortable: false,
    filterable: true,
    width: 170,
    renderCell: (params: GridCellParams) => (
      <span title={params.row.assignedTo?.email || ''}>{params.value as string}</span>
    ),
    valueGetter: (_value, row) => `${row?.assignedTo?.firstName || ''} ${row?.assignedTo?.lastName || ''}`,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'expectedResolutionDate',
    headerName: 'Expected Resolution Date',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'createdAt',
    headerName: 'Created At',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'lll');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'updatedAt',
    headerName: 'Modified Date',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'lll');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'ticketType',
    headerName: 'Type',
    sortable: true,
    filterable: true,
    width: 250,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'proactive',
    headerName: 'Proactive',
    renderCell: (params: GridCellParams) => (params.value ? 'Yes' : 'No'),
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedBooleanOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'resolutionType',
    headerName: 'Resolution Type',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'resolutionFromSource',
    headerName: 'Resolution from Source',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'sourceTrackingNumber',
    headerName: 'Source Tracking Number',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'createdBy.email',
    headerName: 'Created By',
    sortable: false,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    renderCell: (params: GridCellParams) => (
      <span title={params.row.createdBy?.email || ''}>{params.value as string}</span>
    ),
    valueGetter: (_value, row) => `${row?.createdBy?.firstName || ''} ${row?.createdBy?.lastName || ''}`,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'receivedDate',
    headerName: 'Received Date',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'submittedToSourceDate',
    headerName: 'Submitted to Source Date',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'actualResolutionDate',
    headerName: 'Actual Resolution Date',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'firstRun',
    headerName: 'First Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'lastRun',
    headerName: 'Last Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'statusDate',
    headerName: 'Status Date',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'lll');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'customerList',
    headerName: 'Customers',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      if (value === null) return '';
      return (value as Array<{ id: string; name: string; sf_id: string }>).map((v) => v.name).join(', ');
    },
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'apList',
    headerName: 'APs',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      if (value === null) return '';
      return (value as Array<{ id: string; name: string; sf_id: string }>).map((v) => v.name).join(', ');
    },
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'latestComment',
    headerName: 'Latest Comment',
    renderCell: (params: GridCellParams) => (
      <div className="text-black ql-editor !p-0 -mt-[5px] !h-[initial] max-h-full inline-block align-middle !leading-[1.4]">
        {parse(DOMPurify.sanitize((params.value as string) || ''))}
      </div>
    ),
    valueFormatter: (value) => {
      if (value == null) return '';
      const doc = new DOMParser().parseFromString(DOMPurify.sanitize(String(value)), 'text/html');
      return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
    },
    sortable: true,
    filterable: true,
    width: 300,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'latestCommentBy',
    headerName: 'Latest Comment By',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'latestCommentAt',
    headerName: 'Latest Comment At',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'lll');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'assetCount',
    headerName: 'Asset Count',
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'provisionalAssetCount',
    headerName: 'PA Count',
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'rpmAccountCount',
    headerName: 'RA Count',
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'combinedAssetCount',
    headerName: 'CA Count',
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'description',
    headerName: 'Description',
    sortable: true,
    filterable: true,
    width: 350,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  }
];
